import type { Identifiable } from '~/models/identifiable'
import { ControllerState } from './controllerState'
import type { LocalStateRemove } from './stateChange'

export class ControllerStateArray<
  Item extends Identifiable,
> extends ControllerState<Item, Item[]> {
  override getEmptyState(): Item[] {
    return []
  }

  override keepLocalChanges(newstate: Item[]): Item[] {
    if (!this.localChanges.hasChanges()) {
      return newstate
    }

    let result = [...newstate]

    result = this.handleStateRemove(result)
    result = this.handleStateAdd(result)
    result = this.handleStateUpdate(result)

    return result
  }

  override handleStateUpdate(newState: Item[]): Item[] {
    if (!this.localChanges.hasUpdateChanges()) {
      return newState
    }

    return newState.map((change) => {
      const localChange = this.localChanges.getStateUpdate(change)
      if (localChange === undefined) {
        return change
      }

      if (localChange.isDifferent(change)) {
        return localChange.value
      }

      this.localChanges.removeStateChange(localChange)

      return change
    })
  }

  override handleStateAdd(newState: Item[]): Item[] {
    if (!this.localChanges.hasAddChanges()) {
      return newState
    }

    for (const item of newState) {
      const localChange = this.localChanges.getStateAdd(item)
      if (localChange) {
        this.localChanges.removeStateChange(localChange)
      }
    }

    for (const localChange of this.localChanges.getStateAddEntities()) {
      newState.push(localChange.getValue())
    }

    return newState
  }

  override handleStateRemove(newState: Item[]): Item[] {
    if (!this.localChanges.hasRemoveChanges()) {
      return newState
    }

    const activeLocalChanges: LocalStateRemove<Item>[] = []
    for (const item of newState) {
      const localChange = this.localChanges.getStateRemove(item)
      if (localChange) {
        newState = newState.filter(state => !localChange.isSource(state))
      }
    }

    this.localChanges.removeInactiveStateChanges(activeLocalChanges)

    return newState
  }

  override applyLocalChanges() {
    if (!this.data) {
      return
    }

    if (!this.localChanges.hasChanges()) {
      return
    }

    this.data = this.data.map((item) => {
      const localChange = this.localChanges.getStateUpdate(item)

      if (localChange === undefined) {
        return item
      }

      return localChange.value
    })

    for (const localChange of this.localChanges.getStateAddEntities()) {
      this.data.push(localChange.getValue())
    }

    this.data = this.data
      .map((item) => {
        const removedItemState = this.localChanges.getStateRemove(item)
        if (!removedItemState) {
          return item
        }

        return undefined
      })
      .filter(item => item !== undefined)
  }
}
